import { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { api } from '../../lib/api'
import { Store, ShoppingBag, UserCheck, Package, Users, DollarSign, TrendingUp, Activity, Newspaper, Settings } from 'lucide-react'
import { getCache, getCacheStale, setCache } from '../../lib/cache'

const AdminDashboard = () => {
  const [stats, setStats] = useState({
    totalStores: 0,
    activeStores: 0,
    pendingStores: 0,
    pendingStoreOwnerRequests: 0,
    totalUsers: 0,
    totalOrders: 0,
    monthlyOrders: 0,
    monthlyRevenue: 0
  })
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    loadStats()
  }, [])

  const loadStats = async () => {
    const cacheKey = 'admin_dashboard_stats'
    
    // Intentar cargar desde cache primero
    const cachedStats = getCacheStale<typeof stats>(cacheKey)
    if (cachedStats) {
      setStats(cachedStats)
      setLoading(false)
    }
    
    try {
      setLoading(true)
      // Cargar estadísticas de admin (incluye estadísticas de negocios)
      const statsResponse = await api.getAdminStats()
      
      if (statsResponse.success && statsResponse.data) {
        const adminStats = statsResponse.data
        const newStats = {
          totalStores: adminStats.stores?.total || 0,
          activeStores: adminStats.stores?.approved || 0,
          pendingStores: adminStats.stores?.pending || 0,
          totalUsers: adminStats.users?.total || 0,
          monthlyOrders: adminStats.orders?.monthly || 0,
          monthlyRevenue: adminStats.orders?.monthlyRevenue || 0,
          pendingStoreOwnerRequests: 0,
          totalOrders: 0
        }
        
        setStats(prev => ({
          ...prev,
          ...newStats
        }))

        // Cargar solicitudes de dueños de negocios
        try {
          const requestsResponse = await api.get('/admin/store-owner-requests?status=pending')
          const pendingRequests = requestsResponse.data?.data || []
          const pendingCount = Array.isArray(pendingRequests) ? pendingRequests.length : 0
          setStats(prev => ({
            ...prev,
            pendingStoreOwnerRequests: pendingCount
          }))
          newStats.pendingStoreOwnerRequests = pendingCount
        } catch (error: any) {
          console.error('Error loading store owner requests:', error)
          // Si hay error de red y tenemos cache, mantenerlo
          if ((error.code === 'ERR_NETWORK' || error.code === 'ECONNABORTED' || !error.response) && cachedStats) {
            // Ya tenemos los datos del cache, no hacer nada más
          } else {
            setStats(prev => ({
              ...prev,
              pendingStoreOwnerRequests: 0
            }))
          }
        }

        // Cargar estadísticas de pedidos
        try {
          const orderStatsResponse = await api.getAdminOrderStats()
          if (orderStatsResponse.success) {
            const totalOrders = orderStatsResponse.data?.total || 0
            setStats(prev => ({
              ...prev,
              totalOrders
            }))
            newStats.totalOrders = totalOrders
          }
        } catch (error: any) {
          console.error('Error loading order stats:', error)
          // Si hay error de red y tenemos cache, mantenerlo
          if ((error.code === 'ERR_NETWORK' || error.code === 'ECONNABORTED' || !error.response) && cachedStats) {
            // Ya tenemos los datos del cache, no hacer nada más
          }
        }
        
        // Guardar en cache (5 minutos de TTL)
        setCache(cacheKey, newStats, 5 * 60 * 1000)
      } else {
        console.error('Error: Stats response format is incorrect', statsResponse)
      }
    } catch (error: any) {
      console.error('Error loading admin stats:', error)
      
      // Si hay error de red y tenemos datos en cache, mantenerlos
      if ((error.code === 'ERR_NETWORK' || error.code === 'ECONNABORTED' || !error.response) && cachedStats) {
        // Ya tenemos los datos del cache, no hacer nada más
        return
      }
    } finally {
      setLoading(false)
    }
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Cargando estadísticas...</p>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-100">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Dashboard Administrativo</h1>
          <p className="text-gray-600">Vista general de la plataforma ColmaFácil</p>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <div className="bg-white/90 backdrop-blur-sm rounded-2xl shadow-lg border border-white/50 p-6">
            <div className="flex items-center">
              <div className="w-12 h-12 bg-primary-100 rounded-xl flex items-center justify-center mr-4">
                <Store className="h-6 w-6 text-primary-600" />
              </div>
              <div>
                <h3 className="text-sm font-medium text-gray-600 mb-1">Total Negocios</h3>
                <p className="text-2xl font-bold text-gray-900">{stats.totalStores}</p>
              </div>
            </div>
          </div>

          <div className="bg-white/90 backdrop-blur-sm rounded-2xl shadow-lg border border-white/50 p-6">
            <div className="flex items-center">
              <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center mr-4">
                <Store className="h-6 w-6 text-green-600" />
              </div>
              <div>
                <h3 className="text-sm font-medium text-gray-600 mb-1">Negocios Activos</h3>
                <p className="text-2xl font-bold text-gray-900">{stats.activeStores}</p>
              </div>
            </div>
          </div>

          <div className="bg-white/90 backdrop-blur-sm rounded-2xl shadow-lg border border-white/50 p-6">
            <div className="flex items-center">
              <div className="w-12 h-12 bg-yellow-100 rounded-xl flex items-center justify-center mr-4">
                <ShoppingBag className="h-6 w-6 text-yellow-600" />
              </div>
              <div>
                <h3 className="text-sm font-medium text-gray-600 mb-1">Pendientes</h3>
                <p className="text-2xl font-bold text-gray-900">{stats.pendingStores}</p>
              </div>
            </div>
          </div>

          <div className="bg-white/90 backdrop-blur-sm rounded-2xl shadow-lg border border-white/50 p-6">
            <div className="flex items-center">
              <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center mr-4">
                <UserCheck className="h-6 w-6 text-blue-600" />
              </div>
              <div>
                <h3 className="text-sm font-medium text-gray-600 mb-1">Solicitudes</h3>
                <p className="text-2xl font-bold text-gray-900">{stats.pendingStoreOwnerRequests}</p>
              </div>
            </div>
          </div>

          <div className="bg-white/90 backdrop-blur-sm rounded-2xl shadow-lg border border-white/50 p-6">
            <div className="flex items-center">
              <div className="w-12 h-12 bg-purple-100 rounded-xl flex items-center justify-center mr-4">
                <Users className="h-6 w-6 text-purple-600" />
              </div>
              <div>
                <h3 className="text-sm font-medium text-gray-600 mb-1">Total Usuarios</h3>
                <p className="text-2xl font-bold text-gray-900">{stats.totalUsers}</p>
              </div>
            </div>
          </div>

          <div className="bg-white/90 backdrop-blur-sm rounded-2xl shadow-lg border border-white/50 p-6">
            <div className="flex items-center">
              <div className="w-12 h-12 bg-indigo-100 rounded-xl flex items-center justify-center mr-4">
                <Package className="h-6 w-6 text-indigo-600" />
              </div>
              <div>
                <h3 className="text-sm font-medium text-gray-600 mb-1">Total Pedidos</h3>
                <p className="text-2xl font-bold text-gray-900">{stats.totalOrders}</p>
              </div>
            </div>
          </div>

          <div className="bg-white/90 backdrop-blur-sm rounded-2xl shadow-lg border border-white/50 p-6">
            <div className="flex items-center">
              <div className="w-12 h-12 bg-emerald-100 rounded-xl flex items-center justify-center mr-4">
                <TrendingUp className="h-6 w-6 text-emerald-600" />
              </div>
              <div>
                <h3 className="text-sm font-medium text-gray-600 mb-1">Pedidos (30 días)</h3>
                <p className="text-2xl font-bold text-gray-900">{stats.monthlyOrders}</p>
              </div>
            </div>
          </div>

          <div className="bg-white/90 backdrop-blur-sm rounded-2xl shadow-lg border border-white/50 p-6">
            <div className="flex items-center">
              <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center mr-4">
                <DollarSign className="h-6 w-6 text-green-600" />
              </div>
              <div>
                <h3 className="text-sm font-medium text-gray-600 mb-1">Ingresos (30 días)</h3>
                <p className="text-2xl font-bold text-gray-900">RD$ {stats.monthlyRevenue.toLocaleString('es-DO', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Quick Actions */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          <Link
            to="/admin/stores"
            className="bg-white/90 backdrop-blur-sm rounded-2xl shadow-lg border border-white/50 p-6 hover:shadow-xl transition-all duration-200 hover:scale-105"
          >
            <div className="flex items-center mb-4">
              <div className="w-10 h-10 bg-primary-100 rounded-xl flex items-center justify-center mr-3">
                <Store className="h-5 w-5 text-primary-600" />
              </div>
              <h2 className="text-xl font-semibold text-gray-900">Gestión de Negocios</h2>
            </div>
            <p className="text-gray-600 mb-4">Aprueba o rechaza solicitudes de nuevos negocios</p>
            <div className="text-primary-600 font-medium">Gestionar Negocios →</div>
          </Link>

            <Link
            to="/admin/orders"
            className="bg-white/90 backdrop-blur-sm rounded-2xl shadow-lg border border-white/50 p-6 hover:shadow-xl transition-all duration-200 hover:scale-105"
          >
            <div className="flex items-center mb-4">
              <div className="w-10 h-10 bg-indigo-100 rounded-xl flex items-center justify-center mr-3">
                <Package className="h-5 w-5 text-indigo-600" />
              </div>
              <h2 className="text-xl font-semibold text-gray-900">Gestión de Pedidos</h2>
            </div>
            <p className="text-gray-600 mb-4">Visualiza y gestiona todos los pedidos de la plataforma</p>
            <div className="text-indigo-600 font-medium">Ver Pedidos →</div>
            </Link>

            <Link
              to="/admin/categories"
            className="bg-white/90 backdrop-blur-sm rounded-2xl shadow-lg border border-white/50 p-6 hover:shadow-xl transition-all duration-200 hover:scale-105"
          >
            <div className="flex items-center mb-4">
              <div className="w-10 h-10 bg-purple-100 rounded-xl flex items-center justify-center mr-3">
                <ShoppingBag className="h-5 w-5 text-purple-600" />
              </div>
              <h2 className="text-xl font-semibold text-gray-900">Categorías Globales</h2>
            </div>
            <p className="text-gray-600 mb-4">Gestiona las categorías de productos disponibles</p>
            <div className="text-purple-600 font-medium">Gestionar Categorías →</div>
            </Link>

            <Link
              to="/admin/store-owner-requests"
            className="bg-white/90 backdrop-blur-sm rounded-2xl shadow-lg border border-white/50 p-6 hover:shadow-xl transition-all duration-200 hover:scale-105"
          >
            <div className="flex items-center mb-4">
              <div className="w-10 h-10 bg-blue-100 rounded-xl flex items-center justify-center mr-3">
                <UserCheck className="h-5 w-5 text-blue-600" />
              </div>
              <h2 className="text-xl font-semibold text-gray-900">Solicitudes de Dueños</h2>
            </div>
            <p className="text-gray-600 mb-4">Aprueba usuarios que quieren convertirse en dueños</p>
            <div className="text-blue-600 font-medium">Gestionar Solicitudes →</div>
          </Link>

          <Link
            to="/admin/users"
            className="bg-white/90 backdrop-blur-sm rounded-2xl shadow-lg border border-white/50 p-6 hover:shadow-xl transition-all duration-200 hover:scale-105"
          >
            <div className="flex items-center mb-4">
              <div className="w-10 h-10 bg-emerald-100 rounded-xl flex items-center justify-center mr-3">
                <Users className="h-5 w-5 text-emerald-600" />
              </div>
              <h2 className="text-xl font-semibold text-gray-900">Gestión de Usuarios</h2>
            </div>
            <p className="text-gray-600 mb-4">Administra usuarios, roles y permisos</p>
            <div className="text-emerald-600 font-medium">Gestionar Usuarios →</div>
            </Link>

          <Link
            to="/admin/store-owners"
            className="bg-white/90 backdrop-blur-sm rounded-2xl shadow-lg border border-white/50 p-6 hover:shadow-xl transition-all duration-200 hover:scale-105"
          >
            <div className="flex items-center mb-4">
              <div className="w-10 h-10 bg-orange-100 rounded-xl flex items-center justify-center mr-3">
                <Store className="h-5 w-5 text-orange-600" />
              </div>
              <h2 className="text-xl font-semibold text-gray-900">Dueños de Negocios</h2>
          </div>
            <p className="text-gray-600 mb-4">Visualiza y gestiona dueños de negocios</p>
            <div className="text-orange-600 font-medium">Ver Dueños →</div>
          </Link>

          <Link
            to="/admin/system-status"
            className="bg-white/90 backdrop-blur-sm rounded-2xl shadow-lg border border-white/50 p-6 hover:shadow-xl transition-all duration-200 hover:scale-105"
          >
            <div className="flex items-center mb-4">
              <div className="w-10 h-10 bg-red-100 rounded-xl flex items-center justify-center mr-3">
                <Activity className="h-5 w-5 text-red-600" />
              </div>
              <h2 className="text-xl font-semibold text-gray-900">Estado del Sistema</h2>
            </div>
            <p className="text-gray-600 mb-4">Monitorea el estado de servicios y conexiones</p>
            <div className="text-red-600 font-medium">Ver Estado →</div>
          </Link>

          <Link
            to="/admin/news"
            className="bg-white/90 backdrop-blur-sm rounded-2xl shadow-lg border border-white/50 p-6 hover:shadow-xl transition-all duration-200 hover:scale-105"
          >
            <div className="flex items-center mb-4">
              <div className="w-10 h-10 bg-cyan-100 rounded-xl flex items-center justify-center mr-3">
                <Newspaper className="h-5 w-5 text-cyan-600" />
              </div>
              <h2 className="text-xl font-semibold text-gray-900">Gestión de Noticias</h2>
            </div>
            <p className="text-gray-600 mb-4">Administra las noticias del carousel de inicio</p>
            <div className="text-cyan-600 font-medium">Gestionar Noticias →</div>
          </Link>

          <Link
            to="/admin/platform-settings"
            className="bg-white/90 backdrop-blur-sm rounded-2xl shadow-lg border border-white/50 p-6 hover:shadow-xl transition-all duration-200 hover:scale-105"
          >
            <div className="flex items-center mb-4">
              <div className="w-10 h-10 bg-teal-100 rounded-xl flex items-center justify-center mr-3">
                <Settings className="h-5 w-5 text-teal-600" />
              </div>
              <h2 className="text-xl font-semibold text-gray-900">Configuración de Plataforma</h2>
            </div>
            <p className="text-gray-600 mb-4">Gestiona contacto, redes sociales y datos de ColmaFácil</p>
            <div className="text-teal-600 font-medium">Configurar →</div>
          </Link>
        </div>
      </div>
    </div>
  )
}

export default AdminDashboard
